
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../hooks/useSEO';

const ForgotPasswordPage = () => {
  useSEO({
    title: "Reset Password | AM CARDMATE",
    description: "Forgot your password? Reset it quickly and securely to regain access to your AM CARDMATE account.",
    keywords: "forgot password, reset password, password recovery, account recovery"
  });
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});
  
  const { resetPassword } = useAuth();

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors({});
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const { error } = await resetPassword(email);
      
      if (!error) {
        setSent(true);
      } else {
        setErrors({ general: error });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    }
    
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await resetPassword(email);
    } catch (error) {}
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[250px] sm:w-[300px] md:w-[400px] h-[250px] sm:h-[300px] md:h-[400px] bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />

        <div className="max-w-md w-full space-y-8 relative z-10">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl text-white shadow-lg group-hover:shadow-xl transition-all duration-300">
                <FontAwesomeIcon icon="qrcode" className="text-xl" />
              </div>
              <span className="text-h3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AM CARDMATE</span>
            </Link>
          </div>

          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
              <FontAwesomeIcon icon="check" className="text-2xl text-green-600" />
            </div>
            
            <h2 className="text-h2 text-dark mb-2">
              Check your email
            </h2>
            
            <p className="text-body text-gray mb-6">
              We've sent a password reset link to <strong>{email}</strong>
            </p>

            <div className="space-y-4">
              <div className="bg-blue-50 rounded-2xl p-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon icon="info-circle" className="text-blue-500 mt-0.5" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-body-sm font-medium mb-2 text-blue-700">Next steps:</p>
                    <div className="text-body-sm text-blue-700">
                      <div className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">1</span>
                          <span>Check your email inbox</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">2</span>
                          <span>Click the reset link in the email</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">3</span>
                          <span>Create a new password</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">4</span>
                          <span>Sign in with your new password</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-body-sm text-gray">
                <p>Didn't receive the email? Check your spam folder or</p>
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors disabled:opacity-50 inline-flex items-center justify-center mt-2"
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="animate-spin mr-1" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>send it again</span>
                  )}
                </button>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-500 transition-colors font-medium text-body"
              >
                <FontAwesomeIcon icon="arrow-left" />
                <span>Back to sign in</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[300px] md:w-[400px] h-[250px] sm:h-[300px] md:h-[400px] bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl text-white shadow-lg group-hover:shadow-xl transition-all duration-300">
              <FontAwesomeIcon icon="qrcode" className="text-xl" />
            </div>
            <span className="text-h3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AM CARDMATE</span>
          </Link>
          
          <h2 className="text-h2 text-dark">
            Reset your password
          </h2>
          <p className="mt-2 text-body text-gray">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8">
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 rounded-2xl">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500" />
                <p className="text-body-sm text-red-600">{errors.general}</p>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-body-sm font-medium text-dark mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="envelope" className="text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 bg-gray-50 placeholder-gray-500 text-dark rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.email ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="Enter your email address"
                />
              </div>
              {errors.email && <p className="mt-1 text-body-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 text-body font-medium rounded-2xl text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
              >
                {loading ? (
                  <FontAwesomeIcon icon="spinner" className="animate-spin" />
                ) : (
                  <>
                    <FontAwesomeIcon icon="paper-plane" className="mr-2" />
                    <span>Send Reset Link</span>
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-gray hover:text-dark transition-colors font-medium text-body"
            >
              <FontAwesomeIcon icon="arrow-left" />
              <span>Back to sign in</span>
            </Link>
          </div>
        </div>

        <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 sm:p-6">
          <h3 className="text-h5 text-dark mb-3">Need help?</h3>
          <div className="space-y-2 text-body-sm text-gray">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon="question-circle" className="text-blue-500" />
              <span>Make sure you enter the email address you used to sign up</span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon="clock" className="text-purple-500" />
              <span>The reset link will expire in 60 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon="shield-alt" className="text-green-500" />
              <span>Your account security is our priority</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;